import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Trans, useTranslation } from 'next-i18next';
import cx from 'classnames';

import {
  OpportunityCard,
  OpportunityContentLayout,
  OrganizationContentLayout,
  CommunityCard,
  CompanyCard,
  OpportunityPreview,
} from 'components/search/results';
import { AISearchSuggestionBanner } from 'components/AISearchSuggestionBanner/AISearchSuggestionBanner.component';
import { DropdownV3 } from 'components/ComponentV2';
import { ManageSavedLists } from 'components/search/results/ManageSavedLists';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { SearchPageCategory, SearchPageSortBy, SearchPageType } from 'lib/utils/global.search';
import { useGlobalSearch } from 'lib/providers/GlobalSearchProvider';
import { useGlobalSearchData } from 'lib/providers/GlobalSearchDataProvider';
import { OpportunityProvider } from 'lib/providers/OpportunityProvider';
import { useApp } from 'lib/contexts/save-in-app-context';

import 'react-loading-skeleton/dist/skeleton.css';
import { formatToK } from 'utils/format';
import styles from './SearchContent.module.scss';

const OpportunityCardsSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#ffffff99" highlightColor="#fafafa" height={96}>
      <Skeleton count={10} style={{ marginBottom: 8 }} />
    </SkeletonTheme>
  );
};

const OrganizationCardsSkeleton = () => {
  const screens = useResponsive();

  return (
    <>
      {Array.from({ length: 12 }, (_, i) => i + 1).map((_, index) => (
        <SkeletonTheme key={index} baseColor="#ffffff99" highlightColor="#fafafa" height={screens.sm ? 72 : 188} borderRadius={8}>
          <Skeleton />
        </SkeletonTheme>
      ))}
    </>
  );
};

const SearchContent: React.FC = () => {
  const {
    type,
    category,
    sortBy,
    showSavedListsModal,
    isListAdded,
    hasRequestedPageTypeChanged,
    handleSortByChange,
    openSavedListsModal,
    closeSavedListsModal,
  } = useGlobalSearch();
  const notificationInstance = useNotification();
  const { aiSearches } = useApp();
  const [showAISearchBanner, setShowAISearchBanner] = useState<boolean | null>(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [t] = useTranslation('search.results');
  const [tCommon] = useTranslation('common');
  const {
    pageTitle,
    featuredOpportunities,
    isFeaturedOpportunitiesLoading,
    matchesOpportunities,
    isMatchesOpportunitiesLoading,
    universityEvents,
    isUniversityEventsLoading,
    opportunities,
    isOpportunitiesLoading,
    hasMoreOpportunities,
    isOpportunitiesValidating,
    totalOpportunitiesCount,
    selectedOpportunity,
    onSelectOpportunity,
    nextOpportunityPage,
    universityCommunities,
    isUniversityCommunitiesLoading,
    communities,
    isCommunitiesLoading,
    isCommunitiesValidating,
    hasMoreCommunities,
    totalCommunitiesCount,
    nextCommunityPage,
    companies,
    isCompaniesLoading,
    isCompaniesValidating,
    hasMoreCompanies,
    totalCompaniesCount,
    nextCompanyPage,
    filteredRecordsCount,
    isInitialLoading,
  } = useGlobalSearchData();

  const isLoading = useMemo(() => {
    if (isInitialLoading) return isInitialLoading;

    switch (type) {
      case SearchPageType.JOBS:
      case SearchPageType.INTERNSHIPS:
      case SearchPageType.EVENTS:
        return (
          isFeaturedOpportunitiesLoading ||
          isMatchesOpportunitiesLoading ||
          isUniversityEventsLoading ||
          isOpportunitiesLoading ||
          isOpportunitiesValidating
        );
      case SearchPageType.COMMUNITIES:
        return isUniversityCommunitiesLoading || isCommunitiesLoading || isCommunitiesValidating;
      case SearchPageType.COMPANIES:
        return isCompaniesLoading || isCompaniesValidating;
    }
  }, [
    isInitialLoading,
    isFeaturedOpportunitiesLoading,
    isMatchesOpportunitiesLoading,
    isUniversityEventsLoading,
    isOpportunitiesLoading,
    isOpportunitiesValidating,
    isUniversityCommunitiesLoading,
    isCommunitiesLoading,
    isCommunitiesValidating,
    isCompaniesLoading,
    isCompaniesValidating,
  ]);

  const nextPage = useCallback(() => {
    switch (type) {
      case SearchPageType.JOBS:
      case SearchPageType.INTERNSHIPS:
        setShowAISearchBanner((flag) => (flag === null ? flag : true));
        return nextOpportunityPage();
      case SearchPageType.EVENTS:
        return nextOpportunityPage();
      case SearchPageType.COMMUNITIES:
        return nextCommunityPage();
      case SearchPageType.COMPANIES:
        return nextCompanyPage();
    }
  }, [type, nextOpportunityPage, nextCommunityPage, nextCompanyPage, setShowAISearchBanner]);

  useVisibleOnIntersect({
    elRef: bottomRef,
    threshold: 0,
    onIntersect: isLoading ? undefined : nextPage,
    rootMargin: '50px',
  });

  useEffect(() => {
    if (containerRef.current && (isCommunitiesLoading || isCompaniesLoading)) {
      containerRef.current.scrollTo({ top: 0 });
    }
  }, [isCommunitiesLoading, isCompaniesLoading, containerRef]);

  const sortOptions = useMemo(() => {
    return [
      { id: '1', label: tCommon('sortable-params.most-relevant'), value: SearchPageSortBy.MOST_RELEVANT },
      { id: '2', label: tCommon('sortable-params.latest'), value: SearchPageSortBy.LATEST },
      { id: '3', label: tCommon('sortable-params.closing-soonest'), value: SearchPageSortBy.CLOSING_SOONEST },
    ];
  }, [tCommon]);

  const sortByLabel = useMemo(() => {
    return sortOptions.find((option) => option.value === sortBy)?.label;
  }, [sortOptions, sortBy]);

  const openManageSavedLists = useCallback(() => {
    notificationInstance.clear();
    openSavedListsModal();
  }, []);

  const onCloseAISearchBanner = useCallback(() => {
    setShowAISearchBanner(null);
  }, []);

  useEffect(() => {
    if (!isListAdded) return;

    notificationInstance.information({
      message: (
        <div className={styles['list-added']}>
          <Trans t={t} i18nKey="saved-lists.added">
            1
            <button onClick={openManageSavedLists} className={styles['manage']}>
              2
            </button>
          </Trans>
        </div>
      ),
      showClose: true,
      variant: 'small',
      borderRounded: false,
      icon: 'icon_check',
      timeout: 10000,
    });
  }, [isListAdded]);

  return (
    <section
      className={cx(styles.container, { [styles['orgranization-container']]: category === SearchPageCategory.ORGANIZATION })}
      ref={containerRef}
    >
      {<ManageSavedLists isOpen={showSavedListsModal} onClose={closeSavedListsModal} />}
      {category === SearchPageCategory.OPPORTUNITY ? (
        <OpportunityContentLayout>
          <>
            {!hasRequestedPageTypeChanged && pageTitle && (
              <div className={styles['page-title-container']}>
                <p className={styles['title']}>{pageTitle}</p>
              </div>
            )}
            <div className={styles.header}>
              {(type === SearchPageType.JOBS || type === SearchPageType.INTERNSHIPS) && aiSearches.length > 0 && showAISearchBanner && (
                <AISearchSuggestionBanner className={styles['ai-search-suggestion-banner']} onClose={onCloseAISearchBanner} />
              )}
              <p className={styles['result-count']}>
                {isFeaturedOpportunitiesLoading || isMatchesOpportunitiesLoading || isOpportunitiesLoading
                  ? filteredRecordsCount === 1
                    ? t('search-results.total-results-singular')
                    : t('search-results.total-results-plural', { total: formatToK(filteredRecordsCount) })
                  : totalOpportunitiesCount === 1
                  ? t('search-results.total-results-singular')
                  : t('search-results.total-results-plural', { total: formatToK(totalOpportunitiesCount) })}
              </p>
              {sortBy && sortByLabel && (
                <DropdownV3
                  label=""
                  options={sortOptions}
                  value={sortBy}
                  selectedValueLabel={sortByLabel}
                  onChange={handleSortByChange}
                  variant="tertiary"
                  className={styles['sort-dropdown']}
                  dropdownBtnClassName={styles['sort-dropdown-button']}
                  dropdownClassName={styles['sort-dropdown-options']}
                  startMenuFromBottom
                  startIcon="icon_arrow-down-2"
                  startIconClassName={styles.arrow}
                  selectedValueClassName={styles['selected-value']}
                  startIconSize="auto"
                  hideChevron
                  isValid
                />
              )}
            </div>
            <div className={styles['vertical-cards']}>
              {featuredOpportunities.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  onSelect={onSelectOpportunity}
                  isSelected={selectedOpportunity?.id === opportunity.id}
                />
              ))}
              {matchesOpportunities.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  onSelect={onSelectOpportunity}
                  isSelected={selectedOpportunity?.id === opportunity.id}
                />
              ))}
              {type === SearchPageType.EVENTS && universityEvents.length > 0 && (
                <p className={styles['section-header']}>{t('search-results.from-your-university')}</p>
              )}
              {universityEvents.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  onSelect={onSelectOpportunity}
                  isSelected={selectedOpportunity?.id === opportunity.id}
                />
              ))}
              {type === SearchPageType.EVENTS && universityEvents.length > 0 && opportunities.length > 0 && (
                <p className={styles['section-header']}>{t('search-results.other-events')}</p>
              )}
              {opportunities.map((opportunity) => (
                <OpportunityCard
                  key={opportunity.id}
                  opportunity={opportunity}
                  onSelect={onSelectOpportunity}
                  isSelected={selectedOpportunity?.id === opportunity.id}
                />
              ))}
              {isLoading && <OpportunityCardsSkeleton />}
              {hasMoreOpportunities && !isLoading && <div ref={bottomRef} className={styles['bottom-element']}></div>}
            </div>
          </>
          {selectedOpportunity ? (
            <OpportunityProvider opportunity={selectedOpportunity} isSearchPage>
              <OpportunityPreview />
            </OpportunityProvider>
          ) : null}
        </OpportunityContentLayout>
      ) : (
        <OrganizationContentLayout>
          {type === SearchPageType.COMMUNITIES ? (
            <>
              <div className={cx(styles.header, styles.orgranization, styles.communities)}>
                {isUniversityCommunitiesLoading || isCommunitiesLoading
                  ? filteredRecordsCount === 1
                    ? t('search-results.total-results-singular')
                    : t('search-results.total-results-plural', { total: formatToK(filteredRecordsCount) })
                  : totalCommunitiesCount === 1
                  ? t('search-results.total-results-singular')
                  : t('search-results.total-results-plural', { total: formatToK(totalCommunitiesCount) })}
              </div>
              {universityCommunities.length > 0 && <p className={styles['section-header']}>{t('search-results.from-your-university')}</p>}
              {universityCommunities.length > 0 && (
                <div className={styles['grid-cards']}>
                  {universityCommunities.map((community) => (
                    <CommunityCard key={community.id} community={community} />
                  ))}
                </div>
              )}
              {universityCommunities.length > 0 && communities.length > 0 && (
                <p className={styles['section-header']}>{t('search-results.other-communities')}</p>
              )}
              {communities.length > 0 && (
                <div className={styles['grid-cards']}>
                  {communities.map((community) => (
                    <CommunityCard key={community.id} community={community} />
                  ))}
                </div>
              )}
              {isLoading && (
                <div className={styles['grid-cards']}>
                  <OrganizationCardsSkeleton />
                </div>
              )}
              {hasMoreCommunities && !isLoading && <div ref={bottomRef} className={styles['bottom-element']}></div>}
            </>
          ) : (
            <>
              <div className={cx(styles.header, styles.orgranization, styles.companies)}>
                {isCompaniesLoading
                  ? null
                  : totalCompaniesCount === 1
                  ? t('search-results.total-results-singular')
                  : t('search-results.total-results-plural', { total: formatToK(totalCompaniesCount) })}
              </div>
              {companies.length > 0 && (
                <div className={styles['grid-cards']}>
                  {companies.map((company) => (
                    <CompanyCard key={company.id} company={company} />
                  ))}
                </div>
              )}
              {isLoading && (
                <div className={styles['grid-cards']}>
                  <OrganizationCardsSkeleton />
                </div>
              )}
              {hasMoreCompanies && !isLoading && <div ref={bottomRef} className={styles['bottom-element']}></div>}
            </>
          )}
        </OrganizationContentLayout>
      )}
    </section>
  );
};

SearchContent.displayName = 'SearchContent';

export default SearchContent;
